// src/pages/auth/RegisterPage.jsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/axios";
import { toast } from "react-hot-toast";

/* ───────────────── Password strength ───────────────── */
function scorePassword(pw) {
  let score = 0;
  if (!pw) return 0;
  const rules = [/.{8,}/, /[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/];
  rules.forEach((r) => r.test(pw) && score++);
  if (pw.length >= 12) score++;
  return Math.min(score, 5);
}

const meterBars = [
  { w: "w-1/5",  label: "Very weak", color: "bg-rose-400" },
  { w: "w-2/5",  label: "Weak",      color: "bg-orange-400" },
  { w: "w-3/5",  label: "Fair",      color: "bg-yellow-400" },
  { w: "w-4/5",  label: "Strong",    color: "bg-emerald-400" },
  { w: "w-full", label: "Elite",     color: "bg-cyan-400" },
];

/* ───────────────── Page ───────────────── */
export default function RegisterPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company_name: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [capsOn, setCapsOn] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const onBlur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const strength = useMemo(() => scorePassword(form.password), [form.password]);
  const bar = meterBars[Math.max(0, strength - 1)];

  const errors = useMemo(() => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid work email.";
    if (!form.company_name.trim())
      e.company_name = "Your company/organisation is required.";
    if (form.password.length < 8)
      e.password = "Password must be at least 8 characters.";
    return e;
  }, [form]);

  function handleCapsEvent(e) {
    if (typeof e.getModifierState === "function") {
      setCapsOn(e.getModifierState("CapsLock"));
    }
  }

  async function submit(e) {
    e.preventDefault();
    setTouched({ full_name: true, email: true, company_name: true, password: true });
    if (Object.keys(errors).length) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/solo-mdsh-api/auth/register", form);
      toast.success("Request submitted. We’ll notify you once approved.");
      navigate("/login");
    } catch (err) {
      const msg = err?.response?.data?.error || "Registration failed.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0b0d13] text-white">
      <BackgroundFX />

      {/* Floating SOLO.MDSH header (subtle) */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-8 z-0 flex justify-center gap-4 select-none opacity-10"
      >
        <span className="text-6xl font-black tracking-widest animate-[floatY_9s_ease-in-out_infinite]">
          SOLO
        </span>
        <span className="text-6xl font-black animate-[floatY_10s_ease-in-out_infinite]">.</span>
        <span className="text-6xl font-black tracking-widest animate-[floatY_9.5s_ease-in-out_infinite]">
          MDSH
        </span>
      </div>
      <style>{`
        @keyframes floatY { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
      `}</style>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl items-center justify-center px-6">
        <div className="grid w-full gap-10 md:grid-cols-2">
          {/* Left: Brand + copy */}
          <section className="order-2 md:order-1">
            <div className="flex items-center gap-3">
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-white/5 ring-1 ring-white/10 shadow-glow">
                <span className="sr-only">SOLO.MDSH</span>
                <span aria-hidden className="text-cyan-300 font-black tracking-wider">
                  S
                </span>
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-none">
                  <span className="bg-gradient-to-r from-cyan-300 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
                    SOLO.MDSH OS
                  </span>
                </h1>
                <p className="mt-1 text-xs md:text-sm text-white/70">
                  Quantum-Ready • Procurement • Finance • Staffing
                </p>
              </div>
            </div>

            <div className="mt-8 space-y-4">
              <Buzzwords />
              <p className="text-sm text-white/70 max-w-prose">
                “Systems come and go — but orchestration endures.” Enter the control plane where
                budget signals, vendor streams, and rota vectors converge.
              </p>
            </div>
          </section>

          {/* Right: Card */}
          <section className="order-1 md:order-2">
            <div className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-2xl shadow-card">
              {/* glow ring */}
              <div className="pointer-events-none absolute -inset-px rounded-2xl opacity-20 blur-2xl bg-gradient-to-r from-cyan-500/30 via-fuchsia-500/30 to-violet-500/30" />

              <div className="relative border-b border-white/10 px-6 py-5">
                <h2 className="text-lg font-semibold tracking-wide">Create Access</h2>
                <p className="mt-1 text-[12px] text-white/60">
                  Your request goes to admins for approval.
                </p>
              </div>

              <form onSubmit={submit} className="relative p-6 space-y-4">
                <Field
                  label="Full Name"
                  id="full_name"
                  value={form.full_name}
                  onChange={onChange("full_name")}
                  onBlur={onBlur("full_name")}
                  placeholder="Ada Lovelace"
                  error={touched.full_name && errors.full_name}
                />

                <Field
                  label="Work Email"
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  onBlur={onBlur("email")}
                  placeholder="you@company.com"
                  error={touched.email && errors.email}
                />

                <Field
                  label="Company"
                  id="company_name"
                  value={form.company_name}
                  onChange={onChange("company_name")}
                  onBlur={onBlur("company_name")}
                  placeholder="SOLO.MDSH Ltd"
                  error={touched.company_name && errors.company_name}
                />

                {/* Password with toggle + CapsLock indicator */}
                <div>
                  <label htmlFor="password" className="mb-1 block text-sm text-white/80">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPw ? "text" : "password"}
                      value={form.password}
                      onChange={onChange("password")}
                      onBlur={(e) => {
                        onBlur("password")();
                        setCapsOn(false);
                      }}
                      onKeyDown={handleCapsEvent}
                      onKeyUp={handleCapsEvent}
                      onFocus={handleCapsEvent}
                      placeholder="At least 8 characters"
                      aria-invalid={!!(touched.password && errors.password)}
                      aria-describedby="pw-hint"
                      className="w-full rounded-lg border bg-white/5 px-3 py-2 pr-24 text-sm text-white placeholder-white/40 outline-none transition border-white/10 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/30"
                    />
                    {/* Show/Hide */}
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs text-white/70 hover:text-white transition"
                      aria-label={showPw ? "Hide password" : "Show password"}
                    >
                      {showPw ? "Hide" : "Show"}
                    </button>
                    {/* CapsLock pill */}
                    {capsOn && (
                      <span
                        role="status"
                        aria-live="polite"
                        className="absolute right-14 top-1/2 -translate-y-1/2 select-none rounded-md bg-amber-500/20 px-2 py-[2px] text-[10px] font-medium text-amber-200 ring-1 ring-amber-400/30"
                        title="Caps Lock is ON"
                      >
                        CAPS
                      </span>
                    )}
                  </div>

                  {touched.password && errors.password && (
                    <p className="mt-1 text-[12px] text-rose-300">{errors.password}</p>
                  )}

                  {/* Strength meter */}
                  <div className="mt-2">
                    <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${bar.color} ${bar.w}`}
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={5}
                        aria-valuenow={strength}
                        aria-label="Password strength"
                      />
                    </div>
                    <div className="mt-1 flex items-center justify-between text-[12px] text-white/60">
                      <span>
                        Password strength: <span className="text-white/80">{bar.label}</span>
                      </span>
                      <span id="pw-hint" className="hidden md:block">
                        Tip: mix UPPER/lowercase, numbers & symbols. 12+ chars = elite.
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="group relative mt-2 inline-flex w-full items-center justify-center overflow-hidden rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 font-semibold text-cyan-200 transition hover:bg-cyan-500/20 disabled:opacity-60"
                >
                  <span className="absolute inset-0 -z-10 opacity-30 blur-xl bg-gradient-to-r from-cyan-500/40 via-fuchsia-500/40 to-violet-500/40" />
                  {loading ? "Submitting…" : "Request Access"}
                </button>

                <div className="pt-2 text-center text-sm text-white/70">
                  Already registered?{" "}
                  <Link
                    to="/login"
                    className="text-cyan-300 hover:text-cyan-200 underline underline-offset-4"
                  >
                    Sign in
                  </Link>
                </div>
              </form>
            </div>

            <p className="mt-4 text-center text-[12px] text-white/50">
              By continuing you acknowledge the mission: <em>optimize spend, harmonize rota, and
              compress time-to-decision</em>. Welcome to the signal.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

/* ───────────────── Reusable Field ───────────────── */
function Field({ id, label, error, className = "", ...rest }) {
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm text-white/80">
        {label}
      </label>
      <input
        id={id}
        className="w-full rounded-lg border bg-white/5 px-3 py-2 text-sm text-white placeholder-white/40 outline-none transition border-white/10 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/30"
        aria-invalid={!!error}
        {...rest}
      />
      {error && <p className="mt-1 text-[12px] text-rose-300">{error}</p>}
    </div>
  );
}

/* ───────────────── Animated Buzzwords ───────────────── */
function Buzzwords() {
  // animated “ticker” line with masked gradient
  return (
    <div className="relative overflow-hidden rounded-lg border border-white/10 bg-white/5 p-3 backdrop-blur-md">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent [mask-image:linear-gradient(90deg,transparent,black,transparent)]" />
      <p
        className="whitespace-nowrap text-sm text-cyan-200 animate-[marquee_18s_linear_infinite]"
        style={{ animationDelay: "0.6s" }}
      >
        • Autonomous Procurement • Predictive Staffing • Real-time Spend Integrity • Vendor Risk
        Heatmaps • AI Reconciliation • Budget Drift Guardrails • Situation Rooms • Ops Copilot •
        Forecast-Driven POs • Latency-free Insights •
      </p>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

/* ───────────────── Background FX ───────────────── */
function BackgroundFX() {
  return (
    <>
      {/* floating neon blobs */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-96 w-96 rounded-full bg-cyan-600/30 blur-[120px] animate-[floaty_10s_ease-in-out_infinite]" />
      <div className="pointer-events-none absolute top-1/3 -right-40 h-[28rem] w-[28rem] rounded-full bg-fuchsia-600/25 blur-[140px] animate-[floaty_16s_ease-in-out_infinite]" />
      <div className="pointer-events-none absolute bottom-[-10rem] left-1/4 h-[22rem] w-[22rem] rounded-full bg-violet-600/25 blur-[120px] animate-[floaty_12s_ease-in-out_infinite]" />

      {/* 3D perspective grid (bottom half) */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.18] [perspective:900px] [transform-style:preserve-3d]"
      >
        <div className="absolute inset-x-0 bottom-0 h-1/2 [transform:rotateX(60deg)] bg-[linear-gradient(rgba(255,255,255,0.09)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.09)_1px,transparent_1px)] bg-[size:40px_40px]" />
      </div>

      {/* subtle radial vignette */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,255,231,0.10),transparent_55%)]" />

      {/* local keyframes */}
      <style>{`
        @keyframes floaty {
          0%,100% { transform: translateY(0) }
          50%     { transform: translateY(-16px) }
        }
      `}</style>
    </>
  );
}
